import { Controller } from 'react-hook-form'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { wfBlock } from '../../utils/emailTemplateAddEditConstants'
import type { EmailTemplateAddEditFormProps } from '../../types/components'

const workflowTextareaClass = 'mt-1 w-full rounded-lg border p-2 text-sm'
const workflowHeadingClass =
  'mb-2 text-sm font-bold uppercase tracking-wide text-slate-500'

function WorkflowTextareaField({
  label,
  rows,
  registration,
}: {
  label: string
  rows: number
  registration: ReturnType<EmailTemplateAddEditFormProps['page']['register']>
}) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <textarea rows={rows} className={workflowTextareaClass} {...registration} />
    </div>
  )
}

function WorkflowTextareaPair({
  title,
  left,
  right,
}: {
  title: string
  left: { label: string; registration: ReturnType<EmailTemplateAddEditFormProps['page']['register']> }
  right: { label: string; registration: ReturnType<EmailTemplateAddEditFormProps['page']['register']> }
}) {
  return (
    <div className="w-full min-w-0">
      <h3 className={workflowHeadingClass}>{title}</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <WorkflowTextareaField label={left.label} rows={6} registration={left.registration} />
        <WorkflowTextareaField label={right.label} rows={6} registration={right.registration} />
      </div>
    </div>
  )
}

export function EmailTemplateWorkflowBlocksSection({ page }: EmailTemplateAddEditFormProps) {
  const {
    isEdit,
    workflowType: wt,
    isSms,
    isNotification,
    control,
    register,
    registerField,
    showFieldError,
    fieldErrorMessage,
    errors,
  } = page

  if (!isEdit) return null

  return (
    <>
      {wfBlock(wt, 'CO-APPLICANT_INVITE') && (
        <div className="w-full min-w-0">
          <h3 className={workflowHeadingClass}>Co-applicant content</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <WorkflowTextareaField
              label="Underwriting required — yes"
              rows={6}
              registration={register('underwriter_require_yes')}
            />
            <WorkflowTextareaField
              label="Underwriting required — no"
              rows={6}
              registration={register('underwriter_require_no')}
            />
          </div>
          {isSms === '1' && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <WorkflowTextareaField
                label="Yes — SMS"
                rows={4}
                registration={register('underwriter_require_yes_sms')}
              />
              <WorkflowTextareaField
                label="No — SMS"
                rows={4}
                registration={register('underwriter_require_no_sms')}
              />
            </div>
          )}
        </div>
      )}

      {wfBlock(wt, 'PAYMENT_RECEIPT', 'PAYMENT_RECEIPT_ACCESS_FEE') && (
        <WorkflowTextareaPair
          title="Payment receipt"
          left={{
            label: 'Partial payment content',
            registration: register('certificate_partial_payment'),
          }}
          right={{
            label: 'Completed payment content',
            registration: register('certificate_complete_payment'),
          }}
        />
      )}

      {wfBlock(wt, 'INVOICE_REMINDER', 'INVOICE_REMINDER_ACCESS_FEE') && (
        <WorkflowTextareaPair
          title="Invoice reminder"
          left={{ label: 'Contract invoice', registration: register('contract_invoice') }}
          right={{
            label: 'Policy activation invoice',
            registration: register('policy_activation_invoice'),
          }}
        />
      )}

      {wt === 'RESIDENT_PAID_REMINDER' && (
        <WorkflowTextareaPair
          title="Paid reminder"
          left={{ label: '1st date paid reminder', registration: register('paid_reminder_first') }}
          right={{ label: 'Other date paid reminder', registration: register('paid_reminder_other') }}
        />
      )}

      {wfBlock(wt, 'REFUND_PROCESSED', 'REFUND_PROCESSED_ACCESS_FEE') && (
        <div className="w-full min-w-0">
          <h3 className={workflowHeadingClass}>Refund</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <WorkflowTextareaField
              label="Application refund"
              rows={6}
              registration={register('application_refund')}
            />
            <WorkflowTextareaField
              label="Contract refund"
              rows={6}
              registration={register('certificate_refund')}
            />
          </div>
          <div className="mt-2">
            <label className="text-sm">Rent decreased partial refund</label>
            <textarea
              rows={5}
              className={workflowTextareaClass}
              {...register('rent_partial_refund')}
            />
          </div>
        </div>
      )}

      {wfBlock(wt, 'GENERATE_CERTIFICATE', 'GENERATE_CERTIFICATE_ACCESS_FEE') && (
        <WorkflowTextareaPair
          title="Certificate"
          left={{
            label: 'Contract start (adjusted) content',
            registration: register('generate_certificate_lease_pass'),
          }}
          right={{ label: 'Policy updated', registration: register('policy_updated') }}
        />
      )}

      {wt === 'PROPERTY_ADMIN_CREATION_LIBERTY' && (
        <WorkflowTextareaPair
          title="Property admin"
          left={{ label: 'Account creation', registration: register('account_creation') }}
          right={{ label: 'Account reactivation', registration: register('account_reactivation') }}
        />
      )}

      {wt === 'DEFAULT_PAYMENT_STATEMENT' && (
        <div className="w-full min-w-0">
          <h3 className="mb-1 text-sm font-bold uppercase tracking-wide text-slate-500">
            ACH instructions
          </h3>
          <div className="min-h-[180px]">
            <Controller
              name="ach_instructions"
              control={control}
              render={({ field }) => (
                <CKEditor4
                  id="et-ach"
                  value={field.value || ''}
                  onChange={(html) => field.onChange(html)}
                />
              )}
            />
          </div>
          <h3 className="mt-4 text-sm font-bold uppercase text-slate-500">Include attachment</h3>
          <div className="mt-2 flex gap-4">
            <label className="inline-flex items-center gap-2">
              <input type="radio" value="1" {...register('has_attachment')} /> Yes
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="radio" value="0" {...register('has_attachment')} /> No
            </label>
          </div>
        </div>
      )}

      {wfBlock(wt, 'ACTIVE_DEFAULTS_RESIDENTS', 'ACTIVE_DEFAULTS_RESIDENTS_COMPANY') && (
        <div className="w-full min-w-0">
          <h3 className="mb-1 text-sm font-bold uppercase tracking-wide text-slate-500">
            Applicant list content
          </h3>
          <Controller
            name="email_body_table"
            control={control}
            render={({ field }) => (
              <CKEditor4
                id="et-body-table"
                value={field.value || ''}
                onChange={(html) => field.onChange(html)}
              />
            )}
          />
        </div>
      )}

      {isNotification === '1' && (
        <div className="w-full min-w-0">
          <h3 className={workflowHeadingClass}>In-app notification</h3>
          <div>
            <label className="text-sm font-medium">Title <span className="text-red-500">*</span></label>
            <input
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              {...registerField('notification_title')}
            />
            {showFieldError('notification_title') && (
              <p className="mt-0.5 text-sm text-red-600">{fieldErrorMessage('notification_title')}</p>
            )}
          </div>
          <div className="mt-3">
            <label className="text-sm font-medium">Body <span className="text-red-500">*</span> (max 200 characters recommended)</label>
            <textarea
              rows={5}
              className={workflowTextareaClass}
              {...registerField('notification_body')}
            />
            {showFieldError('notification_body') && (
              <p className="mt-0.5 text-sm text-red-600">{fieldErrorMessage('notification_body')}</p>
            )}
          </div>
        </div>
      )}

      {isSms === '1' && (
        <div className="w-full min-w-0">
          <h3 className="mb-1 text-sm font-bold uppercase tracking-wide text-slate-500">
            SMS body <span className="text-red-500">*</span>
          </h3>
          <textarea
            rows={5}
            className="w-full rounded-lg border p-2 text-sm"
            placeholder="SMS body *"
            {...registerField('sms_body')}
          />
          {showFieldError('sms_body') && (
            <p className="mt-1 text-sm text-red-600">{errors.sms_body?.message}</p>
          )}
        </div>
      )}
    </>
  )
}
